import {AxiosResponse} from 'axios';
import axios from '../utils/axios';
import environments from '../utils/environments';

export class AdminService {
    private readonly apiUrl: string;

    constructor() {
        this.apiUrl = environments.apiUrl;
    }

    // Tasks
    getTaskStats(): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/tasks/stats`);
    }

    getFailedTasks(): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/tasks/failed`);
    }

    getActiveTasks(): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/tasks/active`);
    }

    getReservedTasks(): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/tasks/reserved`);
    }

    getQueues(): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/queues`);
    }

    getWorkers(): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/workers`);
    }

    retryTask(taskId: number): Promise<AxiosResponse> {
        return axios.post(`${this.apiUrl}/api/v2/admin/tasks/${taskId}/retry`);
    }

    revokeTask(taskId: string): Promise<AxiosResponse> {
        return axios.post(`${this.apiUrl}/api/v2/admin/tasks/${taskId}/revoke`);
    }

    purgeQueue(queueName: string): Promise<AxiosResponse> {
        return axios.post(`${this.apiUrl}/api/v2/admin/queues/${queueName}/purge`);
    }

    pauseWorkers(): Promise<AxiosResponse> {
        return axios.post(`${this.apiUrl}/api/v2/admin/workers/pause`);
    }

    resumeWorkers(): Promise<AxiosResponse> {
        return axios.post(`${this.apiUrl}/api/v2/admin/workers/resume`);
    }

    // Users
    getUsers(params?: Record<string, any>): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/users`, {params});
    }

    getUser(userId: number): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/users/${userId}`);
    }

    createUser(data: {username: string; password: string; is_admin: boolean}): Promise<AxiosResponse> {
        return axios.post(`${this.apiUrl}/api/v2/admin/users`, data);
    }

    updateUser(userId: number, data: {username?: string; password?: string; is_admin?: boolean}): Promise<AxiosResponse> {
        return axios.put(`${this.apiUrl}/api/v2/admin/users/${userId}`, data);
    }

    deleteUser(userId: number): Promise<AxiosResponse> {
        return axios.delete(`${this.apiUrl}/api/v2/admin/users/${userId}`);
    }

    // Links
    getLinks(params?: Record<string, any>): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/links`, {params});
    }

    getLink(linkId: number): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/links/${linkId}`);
    }

    updateLink(linkId: number, data: Record<string, any>): Promise<AxiosResponse> {
        return axios.put(`${this.apiUrl}/api/v2/admin/links/${linkId}`, data);
    }

    deleteLink(linkId: number): Promise<AxiosResponse> {
        return axios.delete(`${this.apiUrl}/api/v2/admin/links/${linkId}`);
    }

    // Files
    getFiles(params?: Record<string, any>): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/files`, {params});
    }

    getFile(fileId: number): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/files/${fileId}`);
    }

    retryFile(fileId: number): Promise<AxiosResponse> {
        return axios.post(`${this.apiUrl}/api/v2/admin/files/${fileId}/retry`);
    }

    deleteFile(fileId: number): Promise<AxiosResponse> {
        return axios.delete(`${this.apiUrl}/api/v2/admin/files/${fileId}`);
    }

    // Pre-Orders
    getPreOrders(params?: Record<string, any>): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/pre-orders`, {params});
    }

    getPreOrder(preOrderId: number): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/pre-orders/${preOrderId}`);
    }

    createPreOrder(data: {name: string}): Promise<AxiosResponse> {
        return axios.post(`${this.apiUrl}/api/v2/admin/pre-orders`, data);
    }

    updatePreOrder(preOrderId: number, data: {name?: string}): Promise<AxiosResponse> {
        return axios.put(`${this.apiUrl}/api/v2/admin/pre-orders/${preOrderId}`, data);
    }

    deletePreOrder(preOrderId: number): Promise<AxiosResponse> {
        return axios.delete(`${this.apiUrl}/api/v2/admin/pre-orders/${preOrderId}`);
    }

    attachLink(preOrderId: number, linkId: number): Promise<AxiosResponse> {
        return axios.post(`${this.apiUrl}/api/v2/admin/pre-orders/${preOrderId}/links`, {link_id: linkId});
    }

    detachLink(preOrderId: number, linkId: number): Promise<AxiosResponse> {
        return axios.delete(`${this.apiUrl}/api/v2/admin/pre-orders/${preOrderId}/links/${linkId}`);
    }

    // Photofixation
    getPhotoOrders(params?: Record<string, any>): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/photofixation/orders`, {params});
    }

    getPhotoOrder(orderId: number): Promise<AxiosResponse> {
        return axios.get(`${this.apiUrl}/api/v2/admin/photofixation/orders/${orderId}`);
    }

    retryPhotoFile(fileId: number): Promise<AxiosResponse> {
        return axios.post(`${this.apiUrl}/api/v2/admin/photofixation/files/${fileId}/retry`);
    }

    deletePhotoOrder(orderId: number): Promise<AxiosResponse> {
        return axios.delete(`${this.apiUrl}/api/v2/admin/photofixation/orders/${orderId}`);
    }
}

export const adminService = new AdminService();
